/**
 * Beat-reactive presentation (spec 5.3 / 7): a single eased pulse value
 * derived from conductor.beatPhase drives the track edge neon, hazard flash,
 * boost chevron loop, and the signature beat-pulse horizon ring.
 * Photosensitivity: pulses are ≤ 2.5 Hz at any shipped BPM, and reduced
 * motion replaces spikes with a slow glow (spec Section 7).
 *
 * One BeatFx per loaded level (palette baked in) — dispose() before swapping.
 * Zero per-frame allocation: colors are modulated in place via scaleToRef.
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { makeFlatMaterial } from "./scene";
import {
  BEAT_PULSE_DECAY,
  NEON_DIM,
  NEON_GAIN,
  HAZARD_DIM,
  HAZARD_GAIN,
  RING_DISTANCE,
  RING_HEIGHT,
  RING_DIAMETER,
  RING_SCALE_KICK,
  REDUCED_MOTION_GLOW_HZ,
  type WorldPalette,
} from "../config/tuning";

export class BeatFx {
  private ring: Mesh;
  private ringMat: StandardMaterial;
  private neonBase: Color3;
  private hazardBase: Color3;
  private boostBase: Color3;
  private ringBase: Color3;
  private reducedMotion: boolean;

  constructor(
    scene: Scene,
    private readonly neonMat: StandardMaterial,
    private readonly hazardMat: StandardMaterial,
    private readonly boostMat: StandardMaterial,
    palette: WorldPalette,
    reducedMotionSetting: boolean,
  ) {
    this.neonBase = neonMat.emissiveColor.clone();
    this.hazardBase = hazardMat.emissiveColor.clone();
    this.boostBase = boostMat.emissiveColor.clone();

    this.ring = CreateTorus(
      "horizon-ring",
      { diameter: RING_DIAMETER, thickness: 0.7, tessellation: 72 },
      scene,
    );
    this.ring.rotation.x = Math.PI / 2; // face the camera down +z
    this.ringMat = makeFlatMaterial(scene, "mat-horizon-ring", palette.neon, 0.9);
    this.ringMat.fogEnabled = false; // it lives beyond the fog on purpose
    this.ringMat.alpha = 0.85;
    this.ring.material = this.ringMat;
    this.ring.isPickable = false;
    this.ringBase = this.ringMat.emissiveColor.clone();

    this.reducedMotion =
      reducedMotionSetting ||
      (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
  }

  /**
   * @param beat conductor.beat (may be < 0 during pre-roll)
   * @param timeS any monotonic seconds (for the reduced-motion slow glow)
   * @param camZ camera z — the ring rides the horizon ahead of it
   */
  update(beat: number, timeS: number, camZ: number): void {
    const phase = beat >= 0 ? beat - Math.floor(beat) : 1;
    const pulse = this.reducedMotion
      ? 0.5 + 0.25 * Math.sin(2 * Math.PI * REDUCED_MOTION_GLOW_HZ * timeS)
      : Math.exp(-phase * BEAT_PULSE_DECAY);

    this.neonBase.scaleToRef(NEON_DIM + NEON_GAIN * pulse, this.neonMat.emissiveColor);
    this.hazardBase.scaleToRef(HAZARD_DIM + HAZARD_GAIN * pulse, this.hazardMat.emissiveColor);
    // boost chevrons: one brightness loop per beat (sawtooth), spec 5.3
    const loop = this.reducedMotion ? pulse : 1 - phase;
    this.boostBase.scaleToRef(0.6 + 0.7 * loop, this.boostMat.emissiveColor);

    this.ring.position.set(0, RING_HEIGHT, camZ + RING_DISTANCE);
    const s = 1 + RING_SCALE_KICK * pulse;
    this.ring.scaling.set(s, s, s);
    this.ringBase.scaleToRef(0.45 + 0.75 * pulse, this.ringMat.emissiveColor);
  }

  dispose(): void {
    this.ringMat.dispose();
    this.ring.dispose();
  }
}
